import { CardInstance, countTribe } from './synergyHelpers';
import { CombatEvent } from './combatEngine';
import { TribeId } from './data/cards/types';
import {
  TRIBE_DEFINITIONS,
  TribeDefinition,
  TribeSynergyBreakpoint,
  getActiveBreakpoint,
} from './data/tribes';

/**
 * Deferred tribe effects that only the combat loop can resolve, because they
 * need the enemy board, the death stream, or per-attack state.
 *
 * applyFactionAuras handles everything it can do to a board in isolation
 * (stat buffs, flags, the Bajitos hop, the Plaqueros cash-in) and hands the
 * rest back here for combatEngine to consume.
 */
export interface TribeCombatHooks {
  /** Start-of-combat direct damage to random enemies (Slameros). */
  openingDamage: { damage: number; count: number; tribeName: string }[];
  /**
   * Summon a token mid-combat when a unit dies (La Clica). `scope: 'tribe'`
   * only fires on that tribe's own deaths; `'any_friendly'` fires on every
   * friendly death. swarmBuff is applied to all friendly tokens per summon,
   * which is what makes the swarm compound over a long fight.
   */
  deathSummons: {
    tribe: TribeId;
    tribeName: string;
    scope: 'tribe' | 'any_friendly';
    token: { attack: number; health: number };
    swarmBuff?: { attack: number; health: number };
  }[];
  /** Anclas 6: Reborn returns at full Health with a bonus instead of at 1 HP. */
  rebornUpgrade: { fullHealth: boolean; bonus: { attack: number; health: number } } | null;
}

export function emptyTribeCombatHooks(): TribeCombatHooks {
  return { openingDamage: [], deathSummons: [], rebornUpgrade: null };
}

/**
 * Applies tribe synergy auras to a board before combat.
 * Each of the 6 tribes has tiered breakpoints at 2/4/6 units.
 *
 * @param board - The combat board (will be mutated with buffs)
 * @returns Combat events for visualization plus deferred hooks for the engine
 */
export function applyFactionAuras(
  board: CardInstance[],
  rng: () => number = Math.random,
): { events: CombatEvent[]; hooks: TribeCombatHooks } {
  const events: CombatEvent[] = [];
  const hooks = emptyTribeCombatHooks();

  for (const tribe of TRIBE_DEFINITIONS) {
    const count = countTribe(board, tribe.id);
    if (count < 2) continue;

    // Find the highest breakpoint met
    const activeBreakpoint = getActiveBreakpoint(tribe, count);
    if (!activeBreakpoint) continue;

    const tribeUnits = board.filter(u => u.tribe === tribe.id);

    // Apply stat buffs
    if (activeBreakpoint.statBuff) {
      const targets = activeBreakpoint.buffAllFriendlies ? board : tribeUnits;
      const buffedIds: string[] = [];

      for (const unit of targets) {
        unit.attack += activeBreakpoint.statBuff.attack;
        unit.health += activeBreakpoint.statBuff.health;
        buffedIds.push(unit.instanceId);
      }

      if (buffedIds.length > 0) {
        events.push({
          type: 'hero_effect',
          effectId: `${tribe.id}_synergy`,
          details: {
            unitIds: buffedIds,
            attackBuff: activeBreakpoint.statBuff.attack,
            healthBuff: activeBreakpoint.statBuff.health,
            tribeName: tribe.name,
            verb: tribe.verb,
            threshold: activeBreakpoint.threshold,
          }
        });
      }
    }

    // Grant flags (first strike, reborn, overkill, ...)
    if (activeBreakpoint.grantFlags) {
      const { target, flags } = activeBreakpoint.grantFlags;
      let targets: CardInstance[] = [];

      if (target === 'all') {
        targets = tribeUnits;
      } else if (target === 'leftmost') {
        // Leftmost by board position, not by filtered-array order.
        const leftmost = board.find(u => u.tribe === tribe.id);
        if (leftmost) targets = [leftmost];
      } else if (target === 'strongest' && tribeUnits.length > 0) {
        targets = [
          tribeUnits.reduce(
            (best, u) => ((u.attack + u.health) > (best.attack + best.health) ? u : best),
            tribeUnits[0],
          ),
        ];
      }

      if (targets.length > 0) {
        for (const unit of targets) {
          if (!unit.flags) unit.flags = {};
          for (const [flag, on] of Object.entries(flags)) {
            if (on) (unit.flags as Record<string, boolean>)[flag] = true;
          }
        }

        events.push({
          type: 'hero_effect',
          effectId: `${tribe.id}_flag`,
          details: {
            unitIds: targets.map(u => u.instanceId),
            flags: Object.keys(flags),
            tribeName: tribe.name,
            verb: tribe.verb,
          }
        });
      }
    }

    // Plaqueros 6 — cash in accumulated Plaque tags. This is the superlinear
    // step: tags accrue in the tavern, and each one is worth attackPer here.
    if (activeBreakpoint.plaqueCashIn) {
      const { attackPer, healthPer } = activeBreakpoint.plaqueCashIn;
      const cashedIds: string[] = [];
      let totalStacks = 0;

      for (const unit of tribeUnits) {
        const stacks = unit.plaqueStacks ?? 0;
        if (stacks <= 0) continue;
        unit.attack += attackPer * stacks;
        unit.health += healthPer * stacks;
        cashedIds.push(unit.instanceId);
        totalStacks += stacks;
      }

      if (cashedIds.length > 0) {
        events.push({
          type: 'hero_effect',
          effectId: `${tribe.id}_plaque_cash_in`,
          details: {
            unitIds: cashedIds,
            totalStacks,
            attackPer,
            healthPer,
            tribeName: tribe.name,
            verb: tribe.verb,
          }
        });
      }
    }

    // Slameros — opening damage. Deferred: needs the enemy board.
    if (activeBreakpoint.damageRandomEnemy) {
      hooks.openingDamage.push({
        damage: activeBreakpoint.damageRandomEnemy.damage,
        count: activeBreakpoint.damageRandomEnemy.count,
        tribeName: tribe.name,
      });
    }

    // La Clica — mid-combat death summons. Deferred: needs the death stream.
    if (activeBreakpoint.deathSummon) {
      hooks.deathSummons.push({
        tribe: tribe.id,
        tribeName: tribe.name,
        scope: activeBreakpoint.deathSummon.scope,
        token: activeBreakpoint.deathSummon.token,
        swarmBuff: activeBreakpoint.deathSummon.swarmBuff,
      });
    }

    // Anclas 6 — upgraded Reborn. Deferred: applied when a unit actually dies.
    if (activeBreakpoint.rebornUpgrade) {
      hooks.rebornUpgrade = activeBreakpoint.rebornUpgrade;
    }

    // Heal all friendlies
    if (activeBreakpoint.healAllFriendlies) {
      for (const unit of board) {
        unit.health += activeBreakpoint.healAllFriendlies;
      }
      events.push({
        type: 'hero_effect',
        effectId: `${tribe.id}_heal`,
        details: {
          unitIds: board.map(u => u.instanceId),
          amount: activeBreakpoint.healAllFriendlies,
          tribeName: tribe.name,
          verb: tribe.verb,
        }
      });
    }

    // Bajitos — the hop
    if (activeBreakpoint.statManipulation) {
      applyStatManipulation(tribeUnits, activeBreakpoint, tribe, events, rng);
    }

    // La Tocada 6 — attack multiplier. Applied LAST for this tribe so it
    // multiplies the post-buff number, not the base one.
    if (activeBreakpoint.attackMultiplier && activeBreakpoint.attackMultiplier !== 1) {
      const multiplier = activeBreakpoint.attackMultiplier;
      const multipliedIds: string[] = [];
      for (const unit of board) {
        if (unit.attack <= 0) continue;
        unit.attack = Math.floor(unit.attack * multiplier);
        multipliedIds.push(unit.instanceId);
      }
      if (multipliedIds.length > 0) {
        events.push({
          type: 'hero_effect',
          effectId: `${tribe.id}_multiplier`,
          details: {
            unitIds: multipliedIds,
            multiplier,
            tribeName: tribe.name,
            verb: tribe.verb,
          }
        });
      }
    }
  }

  return { events, hooks };
}

/**
 * Apply the Bajitos hop — the stat-rewrite that turns a fat wall into a
 * threat. Each rung rewrites harder than the last.
 */
function applyStatManipulation(
  tribeUnits: CardInstance[],
  breakpoint: TribeSynergyBreakpoint,
  tribe: TribeDefinition,
  events: CombatEvent[],
  rng: () => number,
): void {
  const buffedIds: string[] = [];
  // rng is threaded for determinism parity with the rest of the engine even
  // though no current rung picks a random target.
  void rng;

  switch (breakpoint.statManipulation) {
    case 'boost_higher': {
      // Every Bajito leans further into whatever it's already good at.
      for (const unit of tribeUnits) {
        if (unit.attack >= unit.health) {
          unit.attack += 3;
        } else {
          unit.health += 3;
        }
        buffedIds.push(unit.instanceId);
      }
      break;
    }
    case 'swap_if_beneficial': {
      // Swap attack/health if health > attack (net positive), then bonus
      for (const unit of tribeUnits) {
        if (unit.health > unit.attack) {
          const temp = unit.attack;
          unit.attack = unit.health;
          unit.health = temp;
        }
        if (breakpoint.manipulationBonus) {
          unit.attack += breakpoint.manipulationBonus.attack;
          unit.health += breakpoint.manipulationBonus.health;
        }
        buffedIds.push(unit.instanceId);
      }
      break;
    }
    case 'set_attack_to_health': {
      // The T6 payoff: a 2/40 wall becomes a 43/43 monster. Attack copies
      // Health (never downgrades an already-bigger Attack), then bonus.
      for (const unit of tribeUnits) {
        if (unit.health > unit.attack) {
          unit.attack = unit.health;
        }
        if (breakpoint.manipulationBonus) {
          unit.attack += breakpoint.manipulationBonus.attack;
          unit.health += breakpoint.manipulationBonus.health;
        }
        buffedIds.push(unit.instanceId);
      }
      break;
    }
  }

  if (buffedIds.length > 0) {
    events.push({
      type: 'hero_effect',
      effectId: `${tribe.id}_manipulation`,
      details: {
        unitIds: buffedIds,
        manipulation: breakpoint.statManipulation,
        tribeName: tribe.name,
        verb: tribe.verb,
      }
    });
  }
}
